import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const GOLD = '#D4AF37';
const BG_CARD = '#0d1117';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';
const INPUT_BG = '#0a0f1e';

const cardStyle = { background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24, marginBottom: 16 };
const inputStyle = { width: '100%', background: INPUT_BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const btnGold = { background: GOLD, color: '#000', border: 'none', borderRadius: 10, padding: '12px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: TEXT_SEC, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

const EMPTY_CREATE = { amount: '', purchased_by_name: '', purchased_by_email: '', recipient_name: '', recipient_email: '' };

export default function GiftCards() {
  const [cards, setCards] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemAmount, setRedeemAmount] = useState('');
  const [foundCard, setFoundCard] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => { load(); }, []);

  function load() { api.get('/gift-cards').then(r => setCards(r.data)).catch(() => {}); }
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  async function createCard(e) {
    e.preventDefault();
    try {
      await api.post('/gift-cards', form);
      setForm(EMPTY_CREATE); setShowCreate(false); load();
      showToast('Gift card created!');
    } catch { showToast('Error creating gift card'); }
  }

  async function lookupCard() {
    if (!redeemCode.trim()) return;
    try {
      const r = await api.get(`/gift-cards/${redeemCode.trim().toUpperCase()}`);
      setFoundCard(r.data); setLookupError('');
    } catch { setFoundCard(null); setLookupError('Gift card not found or invalid code.'); }
  }

  async function redeemCard() {
    if (!foundCard || !redeemAmount) return;
    try {
      await api.post(`/gift-cards/${foundCard.code}/redeem`, { amount: parseFloat(redeemAmount) });
      setFoundCard(null); setRedeemCode(''); setRedeemAmount('');
      load(); showToast('Gift card redeemed!');
    } catch (e) {
      showToast(e.response?.data?.error || 'Redemption failed');
    }
  }

  function copyLink(code) {
    navigator.clipboard.writeText(`https://joinmane.com/gift/${code}`);
    showToast('Link copied!');
  }

  const totalValue = cards.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const totalRemaining = cards.reduce((s, c) => s + parseFloat(c.remaining_balance || 0), 0);

  return (
    <AdminLayout>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: BG_CARD, border: `1px solid ${GOLD}`, color: GOLD, padding: '12px 20px', borderRadius: 10, zIndex: 9999, fontWeight: 600 }}>{toast}</div>
      )}
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>Gift Cards</h1>
            <p style={{ color: TEXT_SEC, fontSize: 14, margin: '4px 0 0' }}>{cards.length} issued · ${totalRemaining.toFixed(2)} in active balances</p>
          </div>
          <button style={btnGold} onClick={() => setShowCreate(true)}>+ Create Gift Card</button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Cards Issued', value: cards.length },
            { label: 'Total Value Sold', value: `$${totalValue.toFixed(2)}` },
            { label: 'Remaining Balance', value: `$${totalRemaining.toFixed(2)}` },
          ].map(stat => (
            <div key={stat.label} style={{ ...cardStyle, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ color: GOLD, fontSize: 22, fontWeight: 800 }}>{stat.value}</div>
              <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Redeem section */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h3 style={{ color: '#fff', margin: '0 0 16px', fontSize: 16 }}>Redeem Gift Card</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <input style={{ ...inputStyle, flex: 1 }} placeholder="Enter code (e.g. MANE-AB12CD34)" value={redeemCode} onChange={e => setRedeemCode(e.target.value.toUpperCase())} />
            <button style={btnGold} onClick={lookupCard}>Look Up</button>
          </div>
          {lookupError && <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 8px' }}>{lookupError}</p>}
          {foundCard && (
            <div style={{ background: INPUT_BG, borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: GOLD, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{foundCard.code}</div>
                <div style={{ color: TEXT_SEC, fontSize: 13 }}>For: {foundCard.recipient_name || 'N/A'}</div>
                <div style={{ color: '#10b981', fontWeight: 700, marginTop: 4 }}>Balance: ${parseFloat(foundCard.remaining_balance).toFixed(2)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input style={{ ...inputStyle, width: 100 }} type="number" step="0.01" min="0.01" max={foundCard.remaining_balance} placeholder="Amount" value={redeemAmount} onChange={e => setRedeemAmount(e.target.value)} />
                <button style={btnGold} onClick={redeemCard}>Redeem</button>
              </div>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ ...cardStyle, width: '100%', maxWidth: 500, marginBottom: 0 }}>
              <h3 style={{ color: GOLD, margin: '0 0 20px', fontSize: 16 }}>Create Gift Card</h3>
              <form onSubmit={createCard}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ color: TEXT_SEC, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>AMOUNT ($) *</label>
                    <input style={inputStyle} type="number" step="0.01" min="1" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required placeholder="50.00" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={{ color: TEXT_SEC, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>PURCHASER NAME</label>
                      <input style={inputStyle} value={form.purchased_by_name} onChange={e => setForm({...form, purchased_by_name: e.target.value})} placeholder="Jane Smith" />
                    </div>
                    <div>
                      <label style={{ color: TEXT_SEC, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>PURCHASER EMAIL</label>
                      <input style={inputStyle} type="email" value={form.purchased_by_email} onChange={e => setForm({...form, purchased_by_email: e.target.value})} placeholder="jane@example.com" />
                    </div>
                    <div>
                      <label style={{ color: TEXT_SEC, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>RECIPIENT NAME</label>
                      <input style={inputStyle} value={form.recipient_name} onChange={e => setForm({...form, recipient_name: e.target.value})} placeholder="John Smith" />
                    </div>
                    <div>
                      <label style={{ color: TEXT_SEC, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>RECIPIENT EMAIL</label>
                      <input style={inputStyle} type="email" value={form.recipient_email} onChange={e => setForm({...form, recipient_email: e.target.value})} placeholder="john@example.com" />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                  <button type="submit" style={btnGold}>Create Gift Card</button>
                  <button type="button" style={btnGhost} onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cards list */}
        {cards.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: TEXT_SEC }}>—</div>
            <p style={{ color: TEXT_SEC }}>No gift cards issued yet.</p>
          </div>
        ) : (
          <div style={cardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Code', 'Amount', 'Balance', 'Recipient', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', padding: '0 0 12px', textAlign: 'left', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cards.map((card, i) => (
                  <tr key={card.id} style={{ borderTop: i > 0 ? `1px solid ${BORDER}` : 'none' }}>
                    <td style={{ padding: '12px 0', color: GOLD, fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>{card.code}</td>
                    <td style={{ padding: '12px 0', color: '#fff', fontSize: 13 }}>${parseFloat(card.amount).toFixed(2)}</td>
                    <td style={{ padding: '12px 0', color: parseFloat(card.remaining_balance) > 0 ? '#10b981' : TEXT_SEC, fontWeight: 700, fontSize: 13 }}>
                      ${parseFloat(card.remaining_balance).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px 0', color: TEXT_SEC, fontSize: 12 }}>{card.recipient_name || card.purchased_by_name || '—'}</td>
                    <td style={{ padding: '12px 0' }}>
                      <span style={{
                        background: card.is_active ? '#10b98122' : 'rgba(255,255,255,0.05)',
                        color: card.is_active ? '#10b981' : TEXT_SEC,
                        borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700
                      }}>{card.is_active ? 'ACTIVE' : 'USED'}</span>
                    </td>
                    <td style={{ padding: '12px 0' }}>
                      <button style={{ ...btnGhost, fontSize: 11, padding: '6px 12px' }} onClick={() => copyLink(card.code)}>Copy Link</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
